import { DIAGONAL, ORTHOGONAL, onBoard, opponent, sameSquare } from '../board';
import { faceCode } from '../cards';
import type { MoveOption } from '../movement';
import { Pcg32 } from '../rng';
import type {
  Epitaph,
  Face,
  Inscription,
  LedgerLink,
  MatchEvent,
  MatchState,
  Pawn,
  RecordedMove,
  Side,
  Square,
} from '../types';
import { STAMPS, type OmenKind, type StampId } from './ids';
import { glassTwin, immuneIn, omenAt, omensAt } from './hooks';
import {
  L,
  addBrass,
  addSideStatus,
  bumpCounter,
  bumpSide,
  clearStatus,
  freshRound,
  hasCarving,
  hasRule,
  hasStatus,
  holds,
  houseRule,
  mantelOf,
  markOnce,
  noteColor,
  noteTrigger,
  onceUsed,
  replaceItem,
  ruleN,
  setStatus,
  sideStatus,
  statusOf,
  statusesOf,
  turnLimit,
  untilOwnersNextTurn,
  updateRound,
  useSideStatus,
} from './state';
import type { TallyEffect } from './tally';
import type { ItemSpec, OmenState, StatusInstance } from './types';

/**
 * Long Night match flow (LEDGER.MD): what happens on links, captures, crownings, chain ends,
 * turns and rounds. Every function mutates a cloned state, as the rules procedures do.
 */

type Events = MatchEvent[];
/** Draws a card for a side (the rules' draw step), for Wells, Pockets and the Pocket Watch. */
export type Draw = (s: MatchState, side: Side) => void;

/** Rolls with the luck stream: an integer in [0, n). */
export function luckBelow(s: MatchState, n: number): number {
  const l = L(s);
  const rng = Pcg32.fromState(l.luck);
  const r = rng.nextBelow(n);
  s.ledger = { ...L(s), luck: rng.toState() };
  return r;
}

// ---------------------------------------------------------------------------------------------
// Epitaphs that change hands
// ---------------------------------------------------------------------------------------------

const flipMove = (m: RecordedMove): RecordedMove => {
  switch (m.type) {
    case 'slide':
      return { type: 'slide', dx: m.dx, dy: -m.dy };
    case 'knight':
      return { type: 'knight', dx: m.dx, dy: -m.dy };
    case 'hops':
      return {
        type: 'hops',
        dirs: m.dirs.map((d) => ({ dx: d.dx, dy: (-d.dy || 0) as 1 | 0 | -1 })),
      };
    case 'knights':
      return { type: 'knights', jumps: m.jumps.map((j) => ({ dx: j.dx, dy: -j.dy })) };
    case 'still':
      return m;
  }
};

/**
 * An inscription replayed by the other side: its forward is the other way, so the route is
 * mirrored across the board's middle (a Haunted pawn, the Cracked Mirror, the Planchette).
 */
export const mirrorInscription = (ins: Inscription): Inscription => ({
  ...ins,
  links: ins.links.map((l) => ({ ...l, move: flipMove(l.move) })),
});

/** Who receives the Epitaph of a captured pawn, and the route they receive. */
export function epitaphRecipient(
  s: MatchState,
  victim: Pawn,
): { owner: Side; inscription: Inscription } | null {
  if (!victim.inscription) return null;
  if (hasStatus(s, victim.id, 'hollow')) return null;
  if (hasRule(s, 'mourning-dress', victim.side))
    return { owner: opponent(victim.side), inscription: mirrorInscription(victim.inscription) };
  return { owner: victim.side, inscription: victim.inscription };
}

/** Queues Epitaph arrivals; a second owner in one action waits its turn (LEDGER.MD). */
export function queueArrival(s: MatchState, epitaph: Epitaph): void {
  const prior = s.pendingArrivals;
  if (!prior || prior.owner === epitaph.owner) {
    s.pendingArrivals = { owner: epitaph.owner, epitaphs: [...(prior?.epitaphs ?? []), epitaph] };
    return;
  }
  const l = L(s);
  const queue = l.arrivalQueue ?? [];
  const i = queue.findIndex((q) => q.owner === epitaph.owner);
  const next =
    i >= 0
      ? queue.map((q, j) => (j === i ? { owner: q.owner, epitaphs: [...q.epitaphs, epitaph] } : q))
      : [...queue, { owner: epitaph.owner, epitaphs: [epitaph] }];
  s.ledger = { ...l, arrivalQueue: next };
}

/** Moves the next queued owner's arrivals into place; returns whether there were any. */
export function nextArrivals(s: MatchState): boolean {
  const queue = s.ledger?.arrivalQueue;
  if (!queue || queue.length === 0) return false;
  const [first, ...rest] = queue;
  s.pendingArrivals = first ?? null;
  s.ledger = { ...L(s), arrivalQueue: rest };
  return true;
}

// ---------------------------------------------------------------------------------------------
// Wild collateral
// ---------------------------------------------------------------------------------------------

/** The collateral id of a Wild paid for by Pawn Ticket or a Rag Doll. */
export const FREE_COLLATERAL = '@free';

export function freeWildAvailable(s: MatchState, side: Side): boolean {
  if (!s.ledger) return false;
  if (sideStatus(s, side, 'free-wild')) return true;
  return holds(s, side, 'pawn-ticket') && !onceUsed(s, `ticket:${side}`);
}

/** Pays a Wild with nothing: the Rag Doll first, then Pawn Ticket. */
export function useFreeWild(s: MatchState, side: Side): void {
  if (useSideStatus(s, side, 'free-wild')) return;
  markOnce(s, `ticket:${side}`);
}

/** Whether a pawn pays a Wild without leaving the board (Many Lives, once a round). */
export const staysAsCollateral = (s: MatchState, pawn: Pawn | undefined): boolean =>
  !!pawn && hasCarving(pawn, 'many-lives') && !onceUsed(s, `many-lives:${pawn.id}`);

/** A pawn pawned for a Wild: Blood Money, Debt Collector, The Unredeemed. */
export function afterPawning(s: MatchState, side: Side, pawn: Pawn | undefined, ev: Events): void {
  bumpCounter(s, side, 'pawned');
  if (holds(s, side, 'blood-money'))
    addBrass(s, side, 2, 'blood-money', ev, { key: 'blood-money', max: 6 });
  if (pawn?.inscription && holds(s, side, 'the-unredeemed')) {
    const epitaph: Epitaph = {
      id: `EP${s.nextEpitaphSeq}`,
      owner: side,
      sourcePawnId: pawn.id,
      inscription: pawn.inscription,
    };
    s.nextEpitaphSeq += 1;
    queueArrival(s, epitaph);
  }
}

// ---------------------------------------------------------------------------------------------
// Links, landings and Omens
// ---------------------------------------------------------------------------------------------

/** Squares a slide passes over (not the landing square). */
function passedSquares(from: Square, move: RecordedMove): Square[] {
  if (move.type !== 'slide') return [];
  const dist = Math.max(Math.abs(move.dx), Math.abs(move.dy));
  const ux = Math.sign(move.dx);
  const uy = Math.sign(move.dy);
  const out: Square[] = [];
  for (let i = 1; i < dist; i++) out.push({ x: from.x + ux * i, y: from.y + uy * i });
  return out;
}

/** Squares travelled by a move (Long Way Home): slides their length, jumps 2, hops 2. */
export function pathLength(move: RecordedMove): number {
  switch (move.type) {
    case 'slide':
      return Math.max(Math.abs(move.dx), Math.abs(move.dy));
    case 'knight':
      return 2;
    case 'hops':
      return 2 * move.dirs.length;
    case 'knights':
      return 2 * move.jumps.length;
    case 'still':
      return 0;
  }
}

/** What a link's captures were, read before the move removes them. */
export function captureInfo(s: MatchState, option: MoveOption): LedgerLink['captures'] {
  return option.captures.map((id) => {
    const p = s.pawns[id] as Pawn;
    return {
      id,
      inscribed: !!p.inscription && !hasStatus(s, id, 'hollow'),
      exposed: hasStatus(s, id, 'exposed'),
    };
  });
}

const MIMIC_KINDS: readonly OmenKind[] = [
  'candle',
  'gilt-tile',
  'well',
  'ink-pool',
  'chalk-x',
  'lost-ring',
  'trapdoor',
  'draft',
];

function replaceOmen(s: MatchState, omen: OmenState): void {
  const l = L(s);
  s.ledger = { ...l, omens: l.omens.map((o) => (o.id === omen.id ? omen : o)) };
}

/**
 * Omens landed on during one link (Candle, Gilt Tile, Well, Ink Pool, Chalk X, Lost Ring,
 * Mimic). Trapdoors and Drafts wait for the end of the chain (endOfMove). Returns the Omens as
 * they were landed on, for the Tally.
 */
function landOnOmens(
  s: MatchState,
  side: Side,
  pawnId: string,
  landings: readonly Square[],
  ev: Events,
  draw: Draw,
): { id: number; kind: OmenKind }[] {
  const hit: { id: number; kind: OmenKind }[] = [];
  for (const sq of landings)
    for (let omen of omensAt(s, sq)) {
      if (omen.kind === 'pillar' || omen.kind === 'salt-circle' || omen.kind === 'thorns') continue;
      if (omen.kind === 'trapdoor' || omen.kind === 'draft') continue;
      if (omen.shrouded) {
        omen = { ...omen, shrouded: false };
        replaceOmen(s, omen);
      }
      if (omen.kind === 'mimic') {
        omen = { ...omen, kind: MIMIC_KINDS[luckBelow(s, MIMIC_KINDS.length)] as OmenKind };
        replaceOmen(s, omen);
        if (omen.kind === 'trapdoor' || omen.kind === 'draft') continue;
      }
      ev.push({
        type: 'OmenTriggered',
        side,
        pawnId,
        omenId: omen.id,
        kind: omen.kind,
        square: sq,
      });
      hit.push({ id: omen.id, kind: omen.kind });
      switch (omen.kind) {
        case 'candle': {
          const uses = omen.uses + 1;
          replaceOmen(s, { ...omen, uses, spent: uses >= 3 });
          const k = statusOf(s, pawnId, 'kindled');
          setStatus(s, pawnId, { kind: 'kindled', n: (k?.n ?? 0) + 1 }, ev);
          break;
        }
        case 'gilt-tile':
          if (!onceUsed(s, `gilt:${omen.id}:${side}`)) {
            markOnce(s, `gilt:${omen.id}:${side}`);
            addBrass(s, side, 1, 'gilt-tile', ev);
          }
          break;
        case 'well':
          if (!onceUsed(s, `well:${omen.id}:${side}`)) {
            markOnce(s, `well:${omen.id}:${side}`);
            draw(s, side);
          }
          break;
        case 'lost-ring':
          replaceOmen(s, { ...omen, spent: true });
          addBrass(s, side, 3, 'lost-ring', ev);
          break;
        default:
          break;
      }
    }
  return hit;
}

/** Records and resolves the Long Night side of a link once its move is made. */
export function afterLinkMove(
  s: MatchState,
  side: Side,
  pawnId: string,
  from: Square,
  face: Face,
  wasWild: boolean,
  cardId: string | null,
  option: MoveOption,
  captures: LedgerLink['captures'],
  crowned: boolean,
  newLandings: number,
  ev: Events,
  draw: Draw,
): LedgerLink {
  const omens = landOnOmens(s, side, pawnId, option.landings, ev, draw);
  const thorns = passedSquares(from, option.move).some((sq) =>
    omensAt(s, sq).some((o) => o.kind === 'thorns'),
  );
  if (cardId) {
    noteColor(s, side, face.color);
    const stamp = s.ledger?.cardStamps[cardId] ?? s.cards[cardId]?.stamp;
    if (stamp === 'ward-stamp' && !hasRule(s, 'gleam', side))
      setStatus(s, pawnId, { kind: 'warded', turns: untilOwnersNextTurn(s, side) }, ev);
    const toll = houseRule(s, 'toll', side);
    if (toll) addBrass(s, side, -(ruleN(toll) ?? 1), 'toll', ev);
  }
  return {
    cardId,
    face,
    wasWild,
    landings: option.landings,
    newLandings,
    captures,
    crowned,
    path: pathLength(option.move),
    omens,
    ...(thorns ? { thorns: true } : {}),
  };
}

/** A captured pawn: Pickpocket, Gilded, Thorns, Tin Heart, Homing, Mourning Bell. */
export function afterCapture(s: MatchState, victim: Pawn, byPawnId: string, ev: Events): void {
  const by = s.pawns[byPawnId];
  bumpSide(s, 'lost', victim.side);
  if (by) {
    const steal = houseRule(s, 'pickpocket', victim.side);
    if (steal && by.side !== victim.side)
      addBrass(s, victim.side, -(ruleN(steal) ?? 2), 'pickpocket', ev);
    if (hasStatus(s, by.id, 'gilded') || hasCarving(by, 'gilt-base'))
      addBrass(s, by.side, 1, 'gilded', ev);
    if (hasCarving(victim, 'thorns'))
      setStatus(s, by.id, { kind: 'pinned', turns: untilOwnersNextTurn(s, by.side) }, ev);
  }
  if (hasCarving(victim, 'tin-heart')) addSideStatus(s, victim.side, 'inspired', 1);
  if (hasCarving(victim, 'homing') && !onceUsed(s, `homing:${victim.id}`)) {
    markOnce(s, `homing:${victim.id}`);
    const l2 = L(s);
    s.ledger = { ...l2, homing: [...(l2.homing ?? []), victim.id] };
  }
  for (const st of statusesOf(s, victim.id)) clearStatus(s, victim.id, st.kind, ev);
}

/** A pawn crowned: Gilded, Pockets, Bell Ringer. */
export function afterCrowning(
  s: MatchState,
  side: Side,
  pawnId: string,
  ev: Events,
  draw: Draw,
): void {
  const pawn = s.pawns[pawnId] as Pawn;
  if (hasStatus(s, pawnId, 'gilded') || hasCarving(pawn, 'gilt-base'))
    addBrass(s, side, 1, 'gilded', ev);
  if (hasCarving(pawn, 'pockets')) draw(s, side);
  if (hasCarving(pawn, 'bell-ringer')) {
    const fallen = Object.values(s.pawns)
      .filter((p) => p.side === side && p.status !== 'onBoard')
      .sort((a, b) => (a.id < b.id ? -1 : 1));
    for (const p of fallen) if (returnPawn(s, p.id, 'returned', ev)) break;
  }
}

/** Puts a pawn back on its starting square if it is empty; returns whether it came back. */
export function returnPawn(
  s: MatchState,
  pawnId: string,
  cause: 'homing' | 'returned',
  ev: Events,
): boolean {
  const p = s.pawns[pawnId];
  if (!p || p.status === 'onBoard') return false;
  const taken = Object.values(s.pawns).some(
    (x) => x.status === 'onBoard' && sameSquare(x.square, p.startSquare),
  );
  const pillar = (s.ledger?.omens ?? []).some(
    (o) => o.kind === 'pillar' && !o.spent && sameSquare(o.square, p.startSquare),
  );
  if (taken || pillar) return false;
  s.pawns[pawnId] = { ...p, status: 'onBoard', square: p.startSquare, crowned: false };
  ev.push({ type: 'PawnMoved', side: p.side, pawnId, from: null, to: p.startSquare, cause });
  return true;
}

/**
 * The end of a chain or replay at its final square: Trapdoors, Drafts, Thorns, Lantern, and the
 * statuses a move uses up (Hasted, Phased, Smoldering).
 */
export function endOfMove(
  s: MatchState,
  side: Side,
  pawnId: string,
  links: readonly LedgerLink[],
  ev: Events,
): void {
  if (hasStatus(s, pawnId, 'hasted')) clearStatus(s, pawnId, 'hasted', ev);
  if (hasStatus(s, pawnId, 'phased')) clearStatus(s, pawnId, 'phased', ev);
  if (hasStatus(s, pawnId, 'smoldering')) clearStatus(s, pawnId, 'smoldering', ev);
  const pawn = s.pawns[pawnId];
  if (!pawn || pawn.status !== 'onBoard' || !pawn.square) return;
  if (links.some((l) => l.thorns))
    setStatus(s, pawnId, { kind: 'pinned', turns: untilOwnersNextTurn(s, side) }, ev);
  if (hasCarving(pawn, 'lantern'))
    for (const p of Object.values(s.pawns))
      if (
        p.side === side &&
        p.id !== pawnId &&
        p.status === 'onBoard' &&
        p.square &&
        Math.max(Math.abs(p.square.x - pawn.square.x), Math.abs(p.square.y - pawn.square.y)) === 1
      )
        setStatus(s, p.id, { kind: 'warded', turns: untilOwnersNextTurn(s, side) }, ev);
  for (let omen of omensAt(s, pawn.square)) {
    if (omen.kind !== 'trapdoor' && omen.kind !== 'draft' && omen.kind !== 'mimic') continue;
    if (omen.kind === 'mimic') continue; // a Mimic turns when landed on, not here
    if (omen.shrouded) {
      omen = { ...omen, shrouded: false };
      replaceOmen(s, omen);
    }
    ev.push({
      type: 'OmenTriggered',
      side,
      pawnId,
      omenId: omen.id,
      kind: omen.kind,
      square: pawn.square,
    });
    if (omen.kind === 'trapdoor') {
      const sq = pawn.square;
      s.pawns[pawnId] = { ...pawn, status: 'pawned', square: null };
      ev.push({ type: 'PawnFell', side, pawnId, square: sq, cause: 'trapdoor' });
      return;
    }
    const dirs = [...ORTHOGONAL, ...DIAGONAL];
    const d = dirs[luckBelow(s, dirs.length)] as { dx: number; dy: number };
    const to = { x: pawn.square.x + d.dx, y: pawn.square.y + d.dy };
    const occupied =
      !onBoard(to.x, to.y) ||
      Object.values(s.pawns).some((p) => p.status === 'onBoard' && sameSquare(p.square, to)) ||
      (s.ledger?.omens ?? []).some((o) => o.kind === 'pillar' && sameSquare(o.square, to));
    if (!occupied) {
      s.pawns[pawnId] = { ...pawn, square: to };
      ev.push({ type: 'PawnMoved', side, pawnId, from: pawn.square, to, cause: 'draft' });
    }
    return;
  }
}

/**
 * The Looking Glass (LEDGER.MD §6): a link that ends on one steps out of the one opposite, when
 * that square is free; the chain goes on from there.
 */
export function stepThroughGlass(s: MatchState, side: Side, pawnId: string, ev: Events): void {
  const pawn = s.pawns[pawnId];
  if (!pawn || pawn.status !== 'onBoard' || !pawn.square) return;
  const twin = glassTwin(s, pawn.square);
  if (!twin) return;
  const to = twin.square;
  const taken =
    Object.values(s.pawns).some(
      (p) => p.status === 'onBoard' && p.square && sameSquare(p.square, to),
    ) || !!omenAt(s, to, 'pillar');
  if (taken) return;
  if (twin.shrouded) replaceOmen(s, { ...twin, shrouded: false });
  s.pawns[pawnId] = { ...pawn, square: to };
  ev.push({ type: 'PawnMoved', side, pawnId, from: pawn.square, to, cause: 'glass' });
}

/** Applies what a scored Tally changes (brass, growing items, shattered cards…). */
export function applyTallyEffects(
  s: MatchState,
  side: Side,
  effects: readonly TallyEffect[],
  ev: Events,
): void {
  for (const e of effects) {
    const l = L(s);
    switch (e.kind) {
      case 'brass':
        addBrass(s, side, e.amount, e.reason, ev, e.cap);
        break;
      case 'grow': {
        const item = l.mantel[side].find((i) => i.uid === e.uid);
        if (item) {
          replaceItem(s, side, e.uid, { ...item, value: e.value });
          ev.push({
            type: 'ItemMoved',
            uid: e.uid,
            id: item.id,
            from: side,
            to: side,
            reason: 'grew',
            value: e.value,
          });
        }
        break;
      }
      case 'cardValue':
        s.ledger = { ...l, cardValues: { ...l.cardValues, [e.cardId]: e.value } };
        break;
      case 'pawnValue':
        s.ledger = { ...l, pawnValues: { ...l.pawnValues, [e.key]: e.value } };
        break;
      case 'shatter':
        if (!l.shattered.includes(e.cardId)) {
          s.ledger = { ...l, shattered: [...l.shattered, e.cardId] };
          ev.push({ type: 'CardChanged', side, cardId: e.cardId, change: 'shattered' });
        }
        break;
      case 'trigger':
        noteTrigger(s, e.uid);
        break;
      case 'useSideStatus':
        useSideStatus(s, side, e.status);
        break;
      case 'kindled':
        if (e.n > 0) setStatus(s, e.pawnId, { kind: 'kindled', n: e.n }, ev);
        else clearStatus(s, e.pawnId, 'kindled', ev);
        break;
    }
  }
}

/** Bookkeeping once a chain has been scored: counters, patterns, faces, Ink Pools. */
export function afterChainScored(
  s: MatchState,
  side: Side,
  links: readonly LedgerLink[],
  pattern: string | null,
  secondPattern: string | null | undefined,
): void {
  bumpCounter(s, side, 'chains');
  bumpSide(s, 'chains', side);
  updateRound(s, (r) => ({
    ...r,
    patterns: {
      ...r.patterns,
      [side]: [
        ...r.patterns[side],
        ...(pattern ? [pattern] : []),
        ...(secondPattern ? [secondPattern] : []),
      ],
    },
    firstColor:
      r.firstColor[side] || !links[0]
        ? r.firstColor
        : { ...r.firstColor, [side]: links[0].face.color },
  }));
  const l = L(s);
  const faces = new Set(l.facesPlayed[side]);
  for (const link of links) if (link.cardId) faces.add(faceCode(link.face));
  s.ledger = { ...l, facesPlayed: { ...l.facesPlayed, [side]: [...faces] } };
}

/** Twin Soul: the inscription is copied to every friendly pawn without one. */
export function twinSoul(s: MatchState, pawnId: string, ins: Inscription, ev: Events): void {
  const pawn = s.pawns[pawnId];
  if (!pawn || !hasCarving(pawn, 'twin-soul')) return;
  for (const p of Object.values(s.pawns))
    if (
      p.side === pawn.side &&
      p.id !== pawnId &&
      !p.inscription &&
      !hasStatus(s, p.id, 'hollow')
    ) {
      s.pawns[p.id] = { ...p, inscription: ins };
      ev.push({
        type: 'InscriptionSet',
        side: p.side,
        pawnId: p.id,
        how: 'automatic',
        inscription: ins,
      });
    }
}

/** Rubbings: brass for every Epitaph received. */
export function onEpitaphReceived(s: MatchState, side: Side, ev: Events): void {
  if (holds(s, side, 'rubbings')) addBrass(s, side, 1, 'rubbings', ev);
}

/** Wishbone: 1 in 2, a card drawn by Recover gets a random Stamp for the run. */
export function onRecoverDraw(
  s: MatchState,
  side: Side,
  cardId: string | undefined,
  ev: Events,
): void {
  if (!cardId || !holds(s, side, 'wishbone')) return;
  const odds = holds(s, side, 'rabbits-foot') ? 2 : 1;
  if (luckBelow(s, 2) >= odds) return;
  const stamp = STAMPS[luckBelow(s, STAMPS.length)] as StampId;
  const l = L(s);
  s.ledger = { ...l, cardStamps: { ...l.cardStamps, [cardId]: stamp } };
  ev.push({ type: 'CardChanged', side, cardId, change: 'stamped', stamp });
}

// ---------------------------------------------------------------------------------------------
// Turns
// ---------------------------------------------------------------------------------------------

/** Turn start: count the turn, bring Homing pawns back; returns true when the side must pass. */
export function turnStart(s: MatchState, side: Side, ev: Events): boolean {
  bumpSide(s, 'turns', side);
  const homing = L(s).homing ?? [];
  const mine = homing.filter((id) => s.pawns[id]?.side === side);
  if (mine.length) {
    s.ledger = { ...L(s), homing: homing.filter((id) => !mine.includes(id)) };
    for (const id of mine) returnPawn(s, id, 'homing', ev);
  }
  return L(s).round.turns[side] > turnLimit(s, side);
}

/** Turn end: statuses that last some turns wear off; Smoldering fuses burn down. */
export function turnEnd(s: MatchState, side: Side, ev: Events): void {
  const l = L(s);
  for (const [pawnId, list] of Object.entries(l.statuses)) {
    const pawn = s.pawns[pawnId];
    if (!pawn || pawn.side !== side) continue;
    for (const st of list) {
      if (st.kind === 'smoldering') {
        const fuse = (st.n ?? 2) - 1;
        if (fuse > 0) {
          setStatus(s, pawnId, { ...st, n: fuse }, ev);
          continue;
        }
        clearStatus(s, pawnId, 'smoldering', ev);
        const p = s.pawns[pawnId] as Pawn;
        if (p.status === 'onBoard' && p.square) {
          const sq = p.square;
          s.pawns[pawnId] = { ...p, status: 'pawned', square: null };
          ev.push({ type: 'PawnFell', side: p.side, pawnId, square: sq, cause: 'smoldering' });
        }
        continue;
      }
      if (st.turns === undefined) continue;
      if (st.turns <= 1) clearStatus(s, pawnId, st.kind, ev);
      else setStatus(s, pawnId, { ...st, turns: st.turns - 1 }, ev);
    }
  }
}

/** Hourglass: whether the other side's next turn is skipped (and use it up). */
export function skipsTurn(s: MatchState, side: Side): boolean {
  const l = s.ledger;
  if (!l || l.skipTurns[side] <= 0) return false;
  s.ledger = { ...l, skipTurns: { ...l.skipTurns, [side]: l.skipTurns[side] - 1 } };
  return true;
}

export function notePass(s: MatchState, side: Side): void {
  if (s.ledger && !s.ledger.round.firstPasser) updateRound(s, (r) => ({ ...r, firstPasser: side }));
}

// ---------------------------------------------------------------------------------------------
// Rounds
// ---------------------------------------------------------------------------------------------

function moveItem(
  s: MatchState,
  uid: string,
  from: Side,
  to: Side,
  reason: 'stolen' | 'returned',
  ev: Events,
): void {
  const l = L(s);
  const item = l.mantel[from].find((i) => i.uid === uid);
  if (!item) return;
  s.ledger = {
    ...l,
    mantel: {
      ...l.mantel,
      [from]: l.mantel[from].filter((i) => i.uid !== uid),
      [to]: [...l.mantel[to], item],
    },
  };
  ev.push({ type: 'ItemMoved', uid, id: item.id, from, to, reason });
}

function loseItem(
  s: MatchState,
  side: Side,
  uid: string,
  reason: 'melted' | 'foreclosed',
  ev: Events,
): void {
  const l = L(s);
  const item = l.mantel[side].find((i) => i.uid === uid);
  if (!item) return;
  s.ledger = {
    ...l,
    mantel: { ...l.mantel, [side]: l.mantel[side].filter((i) => i.uid !== uid) },
    lost: { ...l.lost, [side]: [...l.lost[side], uid] },
  };
  ev.push({ type: 'ItemMoved', uid, id: item.id, from: side, to: null, reason });
}

/** Gives every borrowed Superstition back (at the end of a round, or of the match). */
export function returnStolen(
  s: MatchState,
  ev: Events,
  filter: (until: 'round' | 'win') => boolean,
): void {
  const l = L(s);
  const keep = l.stolen.filter((x) => !filter(x.until));
  for (const x of l.stolen)
    if (filter(x.until)) moveItem(s, x.uid, opponent(x.from), x.from, 'returned', ev);
  s.ledger = { ...L(s), stolen: keep };
}

/** Round end: Early Bird, Tallow Candle, Foreclosure, Drip, and stolen items coming back. */
export function roundEnd(s: MatchState, winner: Side | null, ev: Events): void {
  const l = L(s);
  for (const side of ['S', 'N'] as const) {
    if (winner === side && l.round.firstPasser === side && holds(s, side, 'early-bird'))
      addBrass(s, side, 4, 'early-bird', ev);
    if (winner && winner !== side)
      for (const item of mantelOf(s, side).filter((i) => i.id === 'tallow-candle'))
        loseItem(s, side, item.uid, 'melted', ev);
  }
  if (winner === 'N' && hasRule(s, 'foreclosure', 'S')) {
    const mantel = mantelOf(s, 'S').filter((i) => !L(s).stolen.some((x) => x.uid === i.uid));
    if (mantel.length)
      loseItem(s, 'S', (mantel[luckBelow(s, mantel.length)] as ItemSpec).uid, 'foreclosed', ev);
  }
  if (hasRule(s, 'drip', 'S')) {
    const pl = s.players.S;
    pl.spent.push(...pl.hand);
    pl.hand = [];
  }
  returnStolen(s, ev, (until) => until === 'round' || (until === 'win' && winner === 'S'));
}

/** Pawn statuses a round starts with: Spurs, Crowned Head, Warded (Priest, the Ward Favor). */
function startingStatuses(s: MatchState, ev: Events): void {
  for (const p of Object.values(s.pawns)) {
    if (p.status !== 'onBoard') continue;
    if (hasCarving(p, 'spurs')) setStatus(s, p.id, { kind: 'hasted' }, ev);
    if (hasCarving(p, 'crowned-head') && !p.crowned) s.pawns[p.id] = { ...p, crowned: true };
    if (hasRule(s, 'warded-start', p.side)) setStatus(s, p.id, { kind: 'warded', turns: 1 }, ev);
  }
}

/** Takes one of South's Superstitions for the round (Sticky Fingers) or until it wins one. */
function steal(s: MatchState, ev: Events): void {
  const l = L(s);
  const own = l.mantel.S.filter((i) => !l.stolen.some((x) => x.uid === i.uid));
  if (hasRule(s, 'sticky-fingers', 'S') && own.length) {
    const item = own[own.length - 1] as ItemSpec;
    moveItem(s, item.uid, 'S', 'N', 'stolen', ev);
    s.ledger = { ...L(s), stolen: [...L(s).stolen, { uid: item.uid, from: 'S', until: 'round' }] };
  }
  const l2 = L(s);
  if (hasRule(s, 'collateral-call', 'S') && !l2.stolen.some((x) => x.until === 'win')) {
    const left = l2.mantel.S.filter((i) => !l2.stolen.some((x) => x.uid === i.uid));
    if (left.length) {
      const most = [...left].sort(
        (a, b) => (l2.triggers[b.uid] ?? 0) - (l2.triggers[a.uid] ?? 0),
      )[0] as ItemSpec;
      moveItem(s, most.uid, 'S', 'N', 'stolen', ev);
      s.ledger = { ...L(s), stolen: [...L(s).stolen, { uid: most.uid, from: 'S', until: 'win' }] };
    }
  }
}

/**
 * Round setup for the Long Night (after §5.1): Omens reset, statuses clear, Return Stamps go
 * back to the deck, Last Rites keeps the fallen down, stolen goods change hands.
 */
export function roundSetup(
  s: MatchState,
  round: number,
  returned: readonly string[],
  ev: Events,
): void {
  const l = L(s);
  s.ledger = {
    ...l,
    statuses: {},
    sideStatuses: {
      S: l.sideStatuses.S.filter((x) => x.kind === 'free-wild'),
      N: l.sideStatuses.N.filter((x) => x.kind === 'free-wild'),
    },
    omens: l.omens.map((o) => (o.kind === 'lost-ring' ? o : { ...o, uses: 0, spent: false })),
    pileEpitaphs: {},
    skipTurns: { S: 0, N: 0 },
    homing: [],
    arrivalQueue: [],
    round: freshRound(),
  };
  for (const cardId of returned) {
    const card = s.cards[cardId];
    if (!card) continue;
    s.players[card.owner].deck.push(cardId);
    ev.push({ type: 'CardChanged', side: card.owner, cardId, change: 'returned' });
  }
  if (round > 1 && hasRule(s, 'last-rites', 'S')) {
    const prev = s.rounds[s.rounds.length - 1];
    if (prev?.winner !== 'S')
      for (const id of l.fallen ?? []) {
        const p = s.pawns[id];
        if (p && p.side === 'S') s.pawns[id] = { ...p, status: 'captured', square: null };
      }
  }
  startingStatuses(s, ev);
  steal(s, ev);
}

/** Pawns captured during a round, remembered for Last Rites before round setup restores them. */
export function noteFallen(s: MatchState): void {
  const l = s.ledger;
  if (!l) return;
  const fallen = Object.values(s.pawns)
    .filter((p) => p.status === 'captured')
    .map((p) => p.id);
  s.ledger = { ...l, fallen };
}

/** Return Stamp: cards on the pile that go back to their owner's deck instead of Spent. */
export function returningCards(s: MatchState): string[] {
  if (!s.ledger) return [];
  const out: string[] = [];
  for (const e of s.pile) {
    if (e.source !== 'card') continue;
    const card = s.cards[e.cardId];
    if (!card || L(s).shattered.includes(e.cardId)) continue;
    const stamp = s.ledger.cardStamps[e.cardId] ?? card.stamp;
    if (stamp === 'return-stamp' && !hasRule(s, 'gleam', card.owner)) out.push(e.cardId);
  }
  return out;
}

/** Statuses read by the board (for the UI): a pawn's status instances. */
export const pawnStatuses = (
  s: Pick<MatchState, 'ledger'>,
  pawnId: string,
): readonly StatusInstance[] => statusesOf(s, pawnId);

export const isImmuneLedger = (s: MatchState, pawn: Pawn): boolean =>
  immuneIn(s, pawn, opponent(pawn.side));
